import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { ANIMATION_SCALE } from '../../style'

export type SliderContextValue = {
  insideSlider: boolean
  currentSlide?: boolean
}
export const SliderContext = React.createContext<SliderContextValue>({
  insideSlider: false
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SliderProps<T = any> = {
  items: T[]
  keyExtractor?: (item: T, index: number) => string | number
  renderItem: (item: T, index: number, currentSlide: number) => JSX.Element
  currentSlide?: number
  lastMovement?: 'previous' | 'next'
  className?: string
}

const SliderWrap = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const SliderInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  will-change: transform;
`

const Slide = styled.div<{ index: number }>`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  left: ${(props) => props.index * 100}%;
`

const limit = (count: number, value: number) => {
  let result = value
  while (result < 0) {
    result += count
  }
  while (result > count) {
    result -= count
  }
  return result
}

const Slider: React.FC<SliderProps> = ({
  items,
  keyExtractor = (item, i) => i,
  renderItem,
  currentSlide = 0,
  lastMovement,
  className
}) => {
  const [animation] = useState($({ currentSlide }))
  const sliderInner = useRef<HTMLDivElement>(null)

  useEffect(() => {
    animation.stop(true, false)
    const oldSlide = animation.prop('currentSlide')
    let target = currentSlide
    while (lastMovement === 'previous' && target > oldSlide) {
      target -= items.length
    }
    while (lastMovement === 'next' && target < oldSlide) {
      target += items.length
    }
    animation.animate(
      { currentSlide: target },
      {
        duration: (lastMovement ? 600 : 0) * ANIMATION_SCALE,
        easing: 'swing',
        step(now) {
          if (sliderInner.current) {
            sliderInner.current.style.transform = `translateX(${-100 * limit(items.length, now)}%)`
          }
        },
        done() {
          animation.prop('currentSlide', currentSlide)
        }
      }
    )
  }, [items, currentSlide, animation, lastMovement])

  const [appear, setAppear] = useState(true)
  useLayoutEffect(() => {
    setTimeout(() => {
      setAppear(false)
    }, 100)
  })
  const sliderContextValues = useMemo(
    () =>
      items.map(
        (item, i): SliderContextValue => ({
          insideSlider: true,
          currentSlide: !appear && i === currentSlide
        })
      ),
    [currentSlide, items, appear]
  )

  return (
    <SliderWrap className={className}>
      <SliderInner ref={sliderInner}>
        {items.map((item, i) => (
          <Slide index={i} key={keyExtractor(item, i)}>
            <SliderContext.Provider value={sliderContextValues[i]}>
              {renderItem(item, i, currentSlide)}
            </SliderContext.Provider>
          </Slide>
        ))}
        {!!items.length && (
          <Slide index={items.length} key={keyExtractor(items[0], 0) + '-copy'}>
            <SliderContext.Provider value={sliderContextValues[0]}>
              {renderItem(items[0], 0, currentSlide)}
            </SliderContext.Provider>
          </Slide>
        )}
      </SliderInner>
    </SliderWrap>
  )
}

export default Slider
